import { useEffect, useRef } from "react";

export interface ExternalRedirectTarget {
  url: string;
  replaceHistoryEntry?: boolean;
}

/**
 * Performs a one-time, full-page navigation away from the app once `resolveTarget` produces a
 * destination. Resolving to `undefined` leaves the user where they are.
 *
 * This is the **only** sanctioned place to drive `window.location` from React. Reach for it
 * instead of writing an ad-hoc `useEffect` around `window.location`: the navigation is a genuine
 * external-system side effect, and keeping it here means the fire-once guard, the async resolve,
 * and the push-vs-replace choice are implemented once rather than re-derived (and mis-derived) at
 * each call site. For navigation *within* the app, use react-router's `Navigate`/`useNavigate` —
 * not this hook.
 *
 * `resolveTarget` runs at most once per mount — the latest reference is always used, so it does not
 * need to be referentially stable and a re-render mid-resolve cannot start a second attempt. This
 * matters when resolving has side effects (submitting a form before leaving): re-running it would
 * repeat them.
 *
 * @example
 * ```tsx
 * useExternalRedirect(useCallback(async () => {
 *   const { fullURL } = await prepareNextForm()
 *   return { url: fullURL, replaceHistoryEntry: true }
 * }, [prepareNextForm]))
 * ```
 */
export const useExternalRedirect = (
  resolveTarget: (() => Promise<ExternalRedirectTarget | undefined>) | undefined
): void => {
  const hasRedirectedRef = useRef(false);

  const resolveTargetRef = useRef(resolveTarget);
  resolveTargetRef.current = resolveTarget;

  const isEnabled = !!resolveTarget;

  // Navigating the browser away from the app — an external system, not React state.
  useEffect(() => {
    if (!isEnabled || hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;

    const redirect = async () => {
      const target = await resolveTargetRef.current?.();
      if (!target) return;
      if (target.replaceHistoryEntry) window.location.replace(target.url);
      else window.location.assign(target.url);
    };
    redirect();
  }, [isEnabled]);
};
